import * as z from 'zod';

import type {ItemForPriceFragment} from '../item/query';

import type {ProductMaterialOptionCoreFragment} from './query';

export const productMaterialConfigurationSchema = z.object({
	optionKey: z.string(),
	colorKey: z.string(),
});

export type ProductMaterialConfiguration = z.infer<typeof productMaterialConfigurationSchema>;

export function getDefaultProductMaterialConfiguration(
	options: ProductMaterialOptionCoreFragment[],
): ProductMaterialConfiguration | null {
	const firstOption = options.at(0);

	if (
		!firstOption?.material ||
		!firstOption.material.colors ||
		firstOption.material.colors.length < 1
	) {
		return null;
	}
	const firstColor = firstOption.material.colors[0];

	return {
		optionKey: firstOption._key,
		colorKey: firstColor._key,
	};
}

export function getConfiguredMaterialAndColor(
	options: ProductMaterialOptionCoreFragment[],
	configuration: ProductMaterialConfiguration,
) {
	const configuredMaterial = options.find((option) => option._key === configuration.optionKey);
	const configuredColor = configuredMaterial?.material?.colors?.find(
		(color) => color._key === configuration.colorKey,
	);

	if (!configuredMaterial || !configuredColor) {
		return {
			configuredMaterial: null,
			configuredColor: null,
		};
	}

	return {
		configuredMaterial,
		configuredColor,
	};
}

export function productMaterialConfigurationFromFormData(
	options: ProductMaterialOptionCoreFragment[],
	prefix: string,
	formData: FormData,
): ProductMaterialConfiguration | null {
	let config: ProductMaterialConfiguration | null = null;

	const optionAndColor = formData.get(`${prefix}.materialOptionAndColor`);
	if (optionAndColor && typeof optionAndColor === 'string') {
		const [optionKey, colorKey] = optionAndColor.split(':');
		if (optionKey && colorKey) {
			const configuredOption = options.find((option) => option._key === optionKey);
			if (configuredOption) {
				const configuredColor = configuredOption.material?.colors?.find(
					(color) => color._key === colorKey,
				);

				if (configuredColor) {
					config = {
						optionKey: optionKey,
						colorKey: colorKey,
					};
				}
			}
		}
	}

	return config;
}

export function getItemsForProductMaterialConfiguration(
	options: ProductMaterialOptionCoreFragment[],
	configuration: ProductMaterialConfiguration,
): ItemForPriceFragment[] {
	const items: ItemForPriceFragment[] = [];

	const {configuredMaterial, configuredColor} = getConfiguredMaterialAndColor(
		options,
		configuration,
	);

	if (configuredMaterial && configuredColor) {
		if (configuredMaterial.items) {
			items.push(...configuredMaterial.items);
		}
	}

	return items;
}
